import React, {Fragment, useCallback, useEffect, useState,} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {addPopup, dismissMobileAppBanner, setDynamicLink} from 'redux/actions';
import {createAnalyticsItemsList, createGalleryItem, findCategoryById, cleanUpObject} from '../../app/functions';

/* ---------- Constants ---------- */
import {AnalyticsParamValues} from '../../constants/analytics';
import * as routes from 'constants/routes';
import {login} from 'constants/routes';
import {
    HOMEPAGE_BANNER_SLIDER,
    HOMEPAGE_BENEFITS_SLIDER,
    HOMEPAGE_CATEGORY_SLIDER,
    HOMEPAGE_INSURANCE_SLIDER,
    HOMEPAGE_MAGAZINE_SLIDER,
} from '../../constants/homepage-types';

/* ---------- Components ---------- */
import VerticalStrip from './components/vertical-strip';
import ClubStrip from './components/club-strip';
import BenefitsSlider from './components/benefits-slider';
import BannerSlider from './components/banner-slider';
import CategorySlider from './components/category-slider';
import InsuranceSlider from './components/insurance-slider';
import TopSlider from 'containers/home/sections/top-slider/top-slider';
import AppBanner from 'containers/home/sections/app-banner/app-banner';
import HomePagePromotion from 'containers/home/sections/bottom-banners';
import LinkStrip from 'containers/home/sections/link-strip/link-strip';
import SubCategoriesGrid from 'containers/home/sections/sub_categories_grid';
import MobileAppBanner from "containers/home/sections/mobile-app-banner/mobile-app-banner";
import ArticlesStrip from 'containers/magazine/components/ArticlesStrip';

/* ---------- Assets ---------- */
import './index.scss';
import {getCookie} from "../../app/functions";
import * as popups from "../../constants/popup-types";
import {useDeviceState} from "../../app/device_state";

const Home = () => {
    /* ---------- React States ---------- */
    const [topSliderData, setTopSliderData] = useState([]);
    const [, updateState] = useState();
    const forceUpdate = useCallback(() => updateState({}), []);

    /* ---------- Redux Dispatch ---------- */
    const dispatch = useDispatch();

    /* ---------- Redux Data ---------- */
    const Data = useSelector(store => store.data.HomepageData);
    const secondarySlidersData = useSelector(store => store.data.HomepageSecondarySliders) || [];
    const categories = useSelector((store) => cleanUpObject(store?.config?.categoriesArr)?.Data);
    const categoriesMap = useSelector((store) => store?.config?.categoriesMap);
    const showBanner = useSelector(store => store.state.show_mobile_app_banner);
    const appLink = useSelector(store => store.config.AppStoreLink);
    const texts = useSelector(store => store.config.Translations);
    const userData = useSelector(store => store.config.UserData);
    const {appHomeScreenDynamicLink} = useSelector(store => store.config.Parameters);


    const {isMobile, isDesktop} = useDeviceState()
    /* ---------- Device Types ---------- */

    /* ---------- Cookies Check ---------- */
    const isCookies = getCookie('hot_cookies_accepted') === 'true';

    /* ---------- API Homepage Data ---------- */
    useEffect(() => {
        dispatch(setDynamicLink(appHomeScreenDynamicLink));

        const currentURL = window.location.pathname;
        if (decodeURI(currentURL).endsWith(login) && !userData) {
            dispatch(addPopup({
                type: popups.LOGIN
            }))
        }


        return () => {
            dispatch(setDynamicLink(false));
        };
    }, []);

    /* ---------- Close App Banner ---------- */
    const handleCloseClick = () => {
        dispatch(dismissMobileAppBanner());
        forceUpdate();
    };

    const counters = {
        [HOMEPAGE_BANNER_SLIDER]: 0,
        rest: 0,
      }
  

    const renderSecondarySlider = (sliderData, sliderIndex) => {
        let sliderElement = null;
        const promotionName = sliderData['slider'].title;
        let creativeSlot = null;
        switch (Number(sliderData.type_id)) {
            case HOMEPAGE_BENEFITS_SLIDER:
                counters.rest++;
                creativeSlot = `slider_${counters.rest}`
                sliderElement = (
                    <BenefitsSlider
                        key={sliderData.uniqueId}
                        data={sliderData['slider']}
                        creativeSlot={creativeSlot}
                        promotionName={promotionName}
                    />
                );
                break;

            case HOMEPAGE_BANNER_SLIDER:
                const bannerData = sliderData['slider']
                counters[HOMEPAGE_BANNER_SLIDER]++;
                creativeSlot = `between_sliders_${counters[HOMEPAGE_BANNER_SLIDER]}`
                
                let category = categoriesMap?.[String(bannerData.link.parameters.id)] || null;

                const item = createGalleryItem({
                    link: bannerData.link,
                    item_list_name: promotionName,
                    category: category,
                    benefit: bannerData.benefit
                })
                
                sliderElement = (
                    <BannerSlider data={sliderData['slider']}
                                  itemsForLog={[item]}
                                  creativeSlot={creativeSlot}
                                  promotionName={promotionName}
                    />
                );
                break;

            case HOMEPAGE_CATEGORY_SLIDER:
                counters.rest++;
                creativeSlot = `slider_${counters.rest}`
                sliderElement = (
                    <CategorySlider
                        data={sliderData['slider']}
                        creativeSlot={creativeSlot}
                        promotionName={promotionName}
                    />
                );
                break;

            case HOMEPAGE_INSURANCE_SLIDER:
                counters.rest++;
                creativeSlot = `slider_${counters.rest}`
                sliderElement = (
                    <InsuranceSlider data={sliderData['slider']}
                     creativeSlot={creativeSlot}
                    promotionName={promotionName} />
                );
                break;

            case HOMEPAGE_MAGAZINE_SLIDER:
                sliderElement = (
                    <ArticlesStrip
                        key="magazine-slider"
                        uniqueId={sliderData.uniqueId}
                        title={sliderData['slider']['title']}
                        icon={sliderData['slider']['icon_path']}
                        linkText={sliderData['slider']['link_text']}
                        linkRoute={routes.magazine}
                        articles={sliderData['slider']['articles']}
                        analyticsSource={AnalyticsParamValues.HOMEPAGE_MAGAZINE_SOURCE}
                        isHomepage
                    />
                );
                break;
        }

        const includeClubStrip = (
            // Not logged users see it at the beginning of the page
            sliderIndex === 0 && !userData
        ) || (
            // Logged users see it at the end of the page
            sliderIndex === secondarySlidersData.length - 1 && userData
        );

        return (
            <Fragment key={sliderData.uniqueId}>
                {sliderElement && (
                    <section
                        id={sliderData.uniqueId}
                        className="homepage-secondary-slider-wrapper"
                    >
                        {sliderElement}
                    </section>
                )}

                {includeClubStrip && (
                    <ClubStrip key="club-strip"/>
                )}
            </Fragment>
        );
    };

    useEffect(() => {
        setTopSliderData(Data?.['benefits_gallery'] || []);
    }, [Data]);

    let show_link_strip_1, show_link_strip_2, show_app_banner = false;
    let Banners = Data.banners;

    if (Data && Banners) {
        show_link_strip_1 = Banners.hasOwnProperty('top_banner');
        show_link_strip_2 = Banners.hasOwnProperty('bottom_banner');
        show_app_banner = Banners.hasOwnProperty('app_banner');
    }


    return (
        <div className='home-page-wrapper'>
            {(showBanner && !isCookies && isMobile) && (
                <MobileAppBanner
                    dismiss={handleCloseClick}
                    title={texts['web_mobile_app_download_title']}
                    subtitle={texts['web_mobile_app_download_subtitle']}
                    link={appLink}
                />
            )}

        
            {topSliderData.length > 0 && (
                <TopSlider data={topSliderData}/>
            )}

            {secondarySlidersData.length > 0 && (
                secondarySlidersData.map(renderSecondarySlider)
            )}

            {isDesktop && <SubCategoriesGrid/>}
            {Banners && <HomePagePromotion/>}
            {show_app_banner && <AppBanner data={Banners.app_banner}/>}
            {show_link_strip_2 && <LinkStrip data={Banners.bottom_banner}/>}
            {secondarySlidersData.length > 0 && <VerticalStrip sliders={secondarySlidersData}/>}
        </div>
    );
};
export default Home;


